import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, ButtonHandle } from '@progress/kendo-react-buttons';
import { chevronDoubleUpIcon, chevronUpIcon, chevronDownIcon, equalIcon } from '@progress/kendo-svg-icons';
import { Popover, PopoverActionsBar, Tooltip } from '@progress/kendo-react-tooltip';
import { SvgIcon } from '@progress/kendo-react-common';
import { getCriticalityIcon } from '../../../../../Shared/Utilities/criticalityIcon';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../../../messages/en-US';
import SelectedAssetMetricsReadDto, { AlertDetailsDto } from '../../../../../../Models/Dashboard/SelectedAssetMetricsReadDto';
import { AlertsTableComponent } from '../Tables/AlertsTableComponent';
import assetsDashboardApiService from '../../../Utilities/AssetsDashboardApiService';
import alertsApiService from '../../../../../Alerts/Utilities/AlertsApiService';
import { ReactQueryKeys } from '../../../../../../Constants/ClientRoutingConstants';
import { useQuery } from 'react-query';
import LoadingSpinner from '../../../../../Shared/Components/LoadingSpinner';
import ErrorCard from '../../../../../Shared/Components/Cards/ErrorCard';

interface Props {
  assetData: SelectedAssetMetricsReadDto;
  onAlertCountsUpdate?: (newCounts: AlertDetailsDto) => void;
}

export interface AlertsPopoverRef {
  refresh: () => Promise<void>;
}

// Tooltip content as a component
const AlertLegendTooltip = () => {
  const localizationService = useLocalization();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span>
        <b>{localizationService.toLanguageString('custom.alerts', enMessages.custom.alerts)}</b>
      </span>
      <span>
        <SvgIcon icon={chevronDoubleUpIcon} size="small"  style={{ verticalAlign: "middle" }} />: <b>Critical</b>
      </span>
      <span>
        <SvgIcon icon={chevronUpIcon} size="small"  style={{ verticalAlign: "middle" }} />: <b>High</b>
      </span>
      <span>
        <SvgIcon icon={equalIcon} size="small"  style={{ verticalAlign: "middle" }} />: <b>Medium</b>
      </span>
      <span>
        <SvgIcon icon={chevronDownIcon} size="small"  style={{ verticalAlign: "middle" }} />: <b>Low</b>
      </span>
    </div>
  );
};

export const AlertsPopover = forwardRef<AlertsPopoverRef, Props>(({ assetData, onAlertCountsUpdate }, ref) => {
  const localizationService = useLocalization();
  const criticalAnchor = useRef<ButtonHandle | null>(null);
  const highAnchor = useRef<ButtonHandle | null>(null);
  const mediumAnchor = useRef<ButtonHandle | null>(null);
  const lowAnchor = useRef<ButtonHandle | null>(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [selectedAlertType, setSelectedAlertType] = useState<'critical' | 'high' | 'medium' | 'low' | null>(null);
  const [activeAnchor, setActiveAnchor] = useState<ButtonHandle | null>(null);

  const [alertDetails, setAlertDetails] = useState<any>(assetData.alertDetails);

  const capitalizeFirstLetter = (str: string | null) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const onClickAlerts = (alertType: 'critical' | 'high' | 'medium' | 'low') => {
    setSelectedAlertType(alertType);
    setShowAlerts(!showAlerts);
    
    // Set the active anchor based on the clicked button
    switch(alertType) {
      case 'critical':
        setActiveAnchor(criticalAnchor.current);
        break;
      case 'high':
        setActiveAnchor(highAnchor.current);
        break;
      case 'medium':
        setActiveAnchor(mediumAnchor.current);
        break;
      case 'low':
        setActiveAnchor(lowAnchor.current);
        break;
    }
  };

  const assetsMetricsQuery = useQuery<any[]>(
    [ReactQueryKeys.AllTriggeredAlertsQuery, assetData.id], 
    async () => {
      return assetsDashboardApiService.getTreiggeredAlerts(assetData.id);
    }
  );

  // Update alert counts when query data changes
  useEffect(() => {
    if (assetsMetricsQuery.data) {
      const newCounts = {
        critical: assetsMetricsQuery.data.filter((a: any) => a.criticality?.toLowerCase() === 'critical').length,
        high: assetsMetricsQuery.data.filter((a: any) => a.criticality?.toLowerCase() === 'high').length,
        medium: assetsMetricsQuery.data.filter((a: any) => a.criticality?.toLowerCase() === 'medium').length,
        low: assetsMetricsQuery.data.filter((a: any) => a.criticality?.toLowerCase() === 'low').length
      };
      setAlertDetails(newCounts);
    } else {
      // Fallback to asset data if query hasn't loaded yet
      setAlertDetails(assetData.alertDetails);
    }
  }, [assetsMetricsQuery.data, assetData.alertDetails]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      // Just refresh the detailed alerts query for the popover
      await assetsMetricsQuery.refetch();
      // The alert counts will be updated via the assetData prop from the parent
    }
  }));

  const handleResetAlert = async (alertId: string, alert: any) => {
    if (!alertId || !alert) {
      console.error('Missing required parameters for reset');
      return;
    }

    try {
      const alertUpdateDto = {
        name: alert.name,
        description: alert.description,
        enabled: alert.enabled,
        conditions: alert.conditions || [],
        emails: alert.emails || [],
        message: alert.message || ''
      };
      
      await alertsApiService.resetAlert(alertUpdateDto, alertId);
      
      // Refetch alerts data
      const updatedAlerts = await assetsMetricsQuery.refetch();
      
      // Update alertDetails based on the criticality of the reset alert
      if (updatedAlerts.data) {
        const updatedCounts = {
          critical: updatedAlerts.data.filter((a: any) => a.criticality?.toLowerCase() === 'critical').length,
          high: updatedAlerts.data.filter((a: any) => a.criticality?.toLowerCase() === 'high').length,
          medium: updatedAlerts.data.filter((a: any) => a.criticality?.toLowerCase() === 'medium').length,
          low: updatedAlerts.data.filter((a: any) => a.criticality?.toLowerCase() === 'low').length
        };
        
        setAlertDetails(updatedCounts);
      }
    } catch (error: unknown) {
      console.error('Error resetting alert:', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const filteredAlerts = React.useMemo(() => {
    if (!assetsMetricsQuery.data || !selectedAlertType) return assetsMetricsQuery.data;
    
    const filtered = assetsMetricsQuery.data.filter((alert: any) => {
      const matches = alert.criticality?.toLowerCase() === selectedAlertType;
      return matches;
    });

    return filtered;
  }, [assetsMetricsQuery.data, selectedAlertType]);

  const getAlertIcon = (type: 'critical' | 'high' | 'medium' | 'low' | null) =>
    getCriticalityIcon(type);

  if(assetsMetricsQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if(assetsMetricsQuery.isError) {
    return <ErrorCard />;
  }

  return (
    <div className="flex items-center gap-5 justify-center">
      <Tooltip 
        anchorElement="target" 
        position="bottom"
        parentTitle={true}
        content={AlertLegendTooltip}
      >
        <div className="flex items-center">
          <Button 
            fillMode="clear" 
            className="p-0" 
            disabled={alertDetails.critical === 0} 
            ref={criticalAnchor} 
            onClick={() => onClickAlerts('critical')}
            title=" "
          >
            <SvgIcon icon={chevronDoubleUpIcon} size="large" color="#780000" />
            {alertDetails.critical}
          </Button>
          <Button 
            fillMode="clear" 
            className="p-0" 
            disabled={alertDetails.high === 0} 
            ref={highAnchor} 
            onClick={() => onClickAlerts('high')}
            title=" "
          >
            <SvgIcon icon={chevronUpIcon} size="large" color="#dc0000" />
            {alertDetails.high}
          </Button>
          <Button 
            fillMode="clear" 
            className="p-0" 
            disabled={alertDetails.medium === 0} 
            ref={mediumAnchor} 
            onClick={() => onClickAlerts('medium')}
            title=" "
          >
            <SvgIcon icon={equalIcon} size="large" color="#fd8c00" />
            {alertDetails.medium}
          </Button>
          <Button 
            fillMode="clear" 
            className="p-0" 
            disabled={alertDetails.low === 0} 
            ref={lowAnchor} 
            onClick={() => onClickAlerts('low')}
            title=" "
          >
            <SvgIcon icon={chevronDownIcon} size="large" color="#fdc500" />
            {alertDetails.low}
          </Button>
        </div>
      </Tooltip>
      {showAlerts && (
        <Popover
          anchor={activeAnchor?.element}
          show={showAlerts}
          position="bottom"
          collision={{ vertical: "flip", horizontal: "fit" }}
          style={{ maxWidth: '900px' }}
        >
          <div className="flex flex-col gap-2">
            <div
              className="flex items-center gap-2"
              style={{
                fontSize: "1rem",
                padding: "8px 0"
              }}
            >
              {getAlertIcon(selectedAlertType)}
              <span>{capitalizeFirstLetter(selectedAlertType)}</span>
              {localizationService.toLanguageString('custom.alerts', enMessages.custom.alerts)}
            </div>
          </div>
          <AlertsTableComponent 
            assetData={assetData} 
            alerts={filteredAlerts} 
            selectedAlertType={selectedAlertType}
            onResetAlert={handleResetAlert}
          />
          <PopoverActionsBar>
            <div className="grid justify-items-end">
              <Button onClick={() => setShowAlerts(false)}>
                {localizationService.toLanguageString('custom.close', enMessages.custom.close)}
              </Button>
            </div>
          </PopoverActionsBar>
        </Popover>
      )}
    </div>
  );
}); 